/**
 * Admin panel router file
 *
 * @package    src/components
 * @author     WangTuring <wangwang@turing.com>
 * @copyright  2018 Turing Company
 * @license    Turing License
 * @version    2.0
 * @link       https://turing.ly/admin
 */
import { lazy } from 'react';

import DashboardIcon from '@material-ui/icons/Dashboard'
import PeopleIcon from '@material-ui/icons/People'
import EqualizerIcon from '@material-ui/icons/Equalizer'
import Brightness7TwoToneIcon from '@material-ui/icons/Brightness7TwoTone';
import UpdateTwoToneIcon from '@material-ui/icons/UpdateTwoTone';
import ListIcon from '@material-ui/icons/List'
import DeveloperBoardIcon from '@material-ui/icons/DeveloperBoard';
import GolfCourseIcon from '@material-ui/icons/GolfCourse';
import BarChartIcon from '@material-ui/icons/BarChart';
import WorkIcon from '@material-ui/icons/WorkOutline';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import FlipToBackIcon from '@material-ui/icons/FlipToBack';
import BugReportTwoToneIcon from '@material-ui/icons/BugReportTwoTone';
import TuneIcon from '@material-ui/icons/Tune';
import DvrIcon from '@material-ui/icons/Dvr';
import LinkIcon from '@material-ui/icons/Link';
import BallotIcon from '@material-ui/icons/Ballot';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';

const FetchACCVideo = lazy(() => import('../legacy_components/components/admin/FetchUserACCVideo'));
const VetsmithPage = lazy(() => import('../legacy_components/components/admin/Vetsmith/Vetsmith'));
const CreateAccountsFromCSV = lazy(() => import('../legacy_components/components/admin/CreateAccountsFromCSV/CreateAccountsFromCSV'));
const ChangeCandidateEmail = lazy(() => import('../legacy_components/components/admin/ChangeCandidateEmail/ChangeCandidateEmail'));
const DashboardContainer = lazy(() => import('../containers/AdminPanel/DashboardContainer'));
/* legacy_components */

const DeleteUserAccount = lazy(() => import('../legacy_components/components/admin/DeleteUserAccount/DeleteUserAccount'));
const ProblemEditor = lazy(() => import('../legacy_components/components/admin/problemEditor'));
const ChallengeEditor = lazy(() => import('../legacy_components/components/admin/challengeEditor/challengeEditor'));
const ProblemList = lazy(() => import('../legacy_components/components/admin/problemList'));
const ChallengeList = lazy(() => import('../legacy_components/components/admin/ChallengeList'));
const DV2ChallengeScore = lazy(() => import('../legacy_components/components/admin/DV2ChallengeScore/DV2ChallengeScore'));
const JobEditor = lazy(() => import('../legacy_components/components/admin/jobEditor/jobEditor'));
const JobList = lazy(() => import('../legacy_components/components/public/jobList/jobList'));
const JobApplyList = lazy(() => import('../legacy_components/components/admin/JobApplyList/JobApplyList'));
const DesignChallenge = lazy(() => import('../legacy_components/components/admin/designChallenge/DesignChallenge'));

const MCQScoreSummary = lazy(() => import('../legacy_components/components/admin/mcqScoreSummary/McqScoreSummary'));

const BugReportList = lazy(() => import('../legacy_components/components/admin/bugReport/BugReportList'));
const QaAccountUpdate = lazy(() => import('../legacy_components/components/admin/qaAccountsUpdate/QaAccountUpdate'));
const ChallengeStatistics = lazy(() => import('../legacy_components/components/admin/challengeStatistics/ChallengeStatistics'));
const ResetCandidateTest = lazy(() => import('../legacy_components/components/admin/resetCandidateTest/ResetCandidateTest'));
const MCQStatusUpdate = lazy(() => import('../legacy_components/components/admin/MCQStatusUpdate/MCQStatusUpdate'));
const PlaylistAddCheck = lazy(() => import('@material-ui/icons/PlaylistAddCheck'));
const MCQSurvey = lazy(() => import('../legacy_components/components/admin/MCQSurvey/MCQSurvey'));
const MCQScoreUpdate = lazy(() => import('../legacy_components/components/admin/MCQScoreUpdate/MCQScoreUpdate'));

const ChallengeABTestConfig = lazy(() => import('../legacy_components/components/admin/ChallengeABTestConfig/ChallengeABTestConfig'));
const MCQCopyEditor = lazy(() => import('../legacy_components/components/admin/MCQCopyUpdate/MCQCopyEditor'));
const SearchCandidateEmail = lazy(() => import('../legacy_components/components/admin/VettingOpUserLogin/VettingOpUserLogin'));
const InterviewEligibility = lazy(() => import('../legacy_components/components/admin/InterviewEligibility/InterviewEligibility'));
const AreaRelationship = lazy(() => import('../legacy_components/components/admin/SetupAreaRelationship/AreaRelationship'));

const SetupStackDemand = lazy(() => import('../legacy_components/components/admin/StackDemandSetup/SetupStackDemand'));

const UpdateCandidatesStatus = lazy(() => import('../legacy_components/components/admin/UpdateCandidatesStatus/UpdateCandidatesStatus'));
const LiveCodeAdminContainer = lazy(() => import('../legacy_components/LiveCodeInterview/admin'));
const AdminAccountManagement = lazy(() => import('../legacy_components/components/admin/AdminAccountManagement/AdminAccountManagement'));
const RoleActions = lazy(() => import('../constants/RoleActions'));
const AdminChangePassword = lazy(() => import('../legacy_components/components/admin/AdminAccountManagement/AdminChangePassword'));
const ScheduledEvent = lazy(() => import('../legacy_components/components/admin/CalendarScheduledEvent'));
const CalendarSetup = lazy(() => import('../legacy_components/components/admin/AdminCalendarSetup/CalendarSetup'));
const SetupAvailabilityPage = lazy(() => import('../containers/AdminPanel/SetupAvailabilityPage'));
const ListAllCalEvents = lazy(() => import('../legacy_components/components/admin/AdminListAllCalendar/AdminListCalendar'));
const InterviewerAvailabilityReport = lazy(() => import('../legacy_components/components/admin/AdminInterviewerAvailabilityReport/InterviewerAvailabilityReport'));
const InterviewerPerformanceReport = lazy(() => import('../legacy_components/components/admin/AdminInterviewerPerformance/InterviewerPerformanceReport'));
const UserProfileViewPage = lazy(() => import('../legacy_components/components/admin/UserProfileView/UserProfileView'));
const SetupSkillRelationship = lazy(() => import('../legacy_components/components/admin/SetupSkillRelationship/SetupSkillRelationship'));
const SetupStackDemandSkill = lazy(() => import('../legacy_components/components/admin/SetupDemandStackSkills'));
const UpdateCandidateSeniority = lazy(() => import('../legacy_components/components/admin/UpdateCandidateSeniority/UpdateCandidateSeniority'));
const JobSearch = lazy(() => import('../legacy_components/UI_V2_Components/jobs/admin/containers'));
const AdminReferralStatistics = lazy(() => import('../legacy_components/components/admin/AdminReferralManagement/AdminReferralStatistics'));
const DisplayMCQQuestionsPage = lazy(() => import('../legacy_components/components/admin/DisplayMCQQuestions/DisplayMCQQuestions'));
const ProgrammingLanguage = lazy(() => import('../legacy_components/components/admin/ProgrammingLanguage'));
const CreateTestAccount = lazy(() => import('../legacy_components/UI_V2_Components/TestAccount'));
const CreateAccountByResumePage = lazy(() => import('../legacy_components/components/admin/CreateAccountByResume/CreateAccountByResume'));
const SParamManagement = lazy(() => import('../legacy_components/components/admin/SParamManagement/SParamManagement'));
const ContentManagementSystem = lazy(() => import('../legacy_components/components/admin/ContentManagementSystem/ContentManagementSystem'));
const SetupRecommendationMCQ = lazy(() => import('../legacy_components/UI_V2_Components/GetMatched/admin/SetupRecommendationMCQ'));
const CloudCertificationVerification = lazy(() => import('../legacy_components/components/admin/CloudCertificationVerification'));
const CloudCertificates = lazy(() => import('../legacy_components/components/admin/CloudCertficates'));
/* End legacy_components */

const CloudLabsProfiles = lazy(() => import('../../cloud_institute_integration/admin/LabsProfiles'));
const CloudLabsInstances = lazy(() => import('../../cloud_institute_integration/admin/LabsInstances'));
const FastTrackDevelopers = lazy(() => import('../legacy_components/components/admin/FastTrackDevelopers'));
const FastTrackDevelopersCreate = lazy(() => import('../legacy_components/components/admin/FastTrackDevelopers/FastTrackDevelopersCreate'));

/**
 * Admin panel routes
 */
const AdminPanelRoutes = [
  {
    path: '/admin/dashboard',
    sidebarName: 'Dashboard',
    roleAction: RoleActions.DASHBOARD,
    navbarName: 'Admin Panel / Dashboard',
    icon: DashboardIcon,
    component: DashboardContainer
  },
  /* legacy_components */
  // {
  //   path: '/admin/problemEditor/new',
  //   sidebarName: 'Problem Editor',
  //   roleAction: RoleActions.PROBLEM_EDITOR,
  //   navbarName: 'Admin Panel / Problem Editor',
  //   icon: DeveloperBoardIcon,
  //   component: ProblemEditor
  // },
  // {
  //   path: '/admin/problems',
  //   sidebarName: 'All Problems',
  //   roleAction: RoleActions.ALL_PROBLEMS,
  //   navbarName: 'Admin Panel / All Problems',
  //   icon: ListIcon,
  //   component: ProblemList
  // },
  // {
  //   path: '/admin/challengeEditor',
  //   sidebarName: 'Challenge Editor',
  //   roleAction: RoleActions.CHALLENGE_EDITOR,
  //   navbarName: 'Admin Panel / Challenge Editor',
  //   icon: GolfCourseIcon,
  //   component: ChallengeEditor
  // },
  // {
  //   path: '/admin/dv2_challenge_list',
  //   sidebarName: 'All Challenges',
  //   roleAction: RoleActions.ALL_CHALLENGES,
  //   navbarName: 'Admin Panel / All Challenges',
  //   icon: ListIcon,
  //   component: ChallengeList
  // },
  // {
  //   path: '/admin/dv2-challenge-score',
  //   sidebarName: 'DV2 Challenge Score',
  //   roleAction: RoleActions.DV2_CHALLENGE_SCORE,
  //   navbarName: 'Admin Panel / DV2 Challenge Score',
  //   icon: BarChartIcon,
  //   component: DV2ChallengeScore
  // },
  {
    path: '/admin/ci-labs-profiles',
    sidebarName: 'Cloud Labs Profiles',
    roleAction: RoleActions.CLOUD_INSTITUTE,
    navbarName: 'Cloud / Lab Profiles',
    icon: ListIcon,
    component: CloudLabsProfiles,
  },
  {
    path: '/admin/ci-labs-instances',
    sidebarName: 'Cloud Labs Instances',
    roleAction: RoleActions.CLOUD_INSTITUTE,
    navbarName: 'Cloud / Lab Instances',
    icon: ListIcon,
    component: CloudLabsInstances,
  },
  {
    path: '/admin/jobEditor',
    sidebarName: 'Job Editor',
    roleAction: RoleActions.JOB_EDITOR,
    navbarName: 'Admin Panel / Job Editor',
    icon: WorkIcon,
    component: JobEditor
  },
  {
    path: '/admin/jobList',
    sidebarName: 'Job List',
    roleAction: RoleActions.JOB_LIST,
    navbarName: 'Admin Panel / Job List',
    icon: BusinessCenterIcon,
    component: JobList
  },
  {
    path: '/admin/jobApplyList',
    sidebarName: 'Job Apply List',
    roleAction: RoleActions.JOB_APPLY_LIST,
    navbarName: 'Admin Panel / Job Apply List',
    icon: BusinessCenterIcon,
    component: JobApplyList
  },
  {
    path: '/admin/designChallengeList',
    sidebarName: 'Design Challenge List',
    roleAction: RoleActions.DESIGN_CHALLENGE_LIST,
    navbarName: 'Admin Panel / Design Challenge List',
    icon: BusinessCenterIcon,
    component: DesignChallenge
  },
  // {
  //   path: '/admin/mcqScoreSummary',
  //   sidebarName: 'MCQ Score Summary',
  //   roleAction: RoleActions.MCQ_SCORE_SUMMARY,
  //   navbarName: 'Admin Panel / MCQ Score Summary',
  //   icon: FlipToBackIcon,
  //   component: MCQScoreSummary
  // },
  {
    path: '/admin/bugReport',
    sidebarName: 'Bug Report List',
    roleAction: RoleActions.BUG_REPORT_LIST,
    navbarName: 'Admin Panel / Bug Report List',
    icon: BugReportTwoToneIcon,
    component: BugReportList
  },
  {
    path: '/admin/qaAccount',
    sidebarName: 'QA Accounts List',
    roleAction: RoleActions.QA_ACCOUNTS_LIST,
    navbarName: 'Admin Panel / QA Accounts List',
    icon: PeopleIcon,
    component: QaAccountUpdate
  },
  {
    path: '/admin/challengeStatistics',
    sidebarName: 'Challenge Statistics',
    roleAction: RoleActions.CHALLENGE_STATISTICS,
    navbarName: 'Admin Panel / Challenge Statistics',
    icon: EqualizerIcon,
    component: ChallengeStatistics
  },
  // {
  //   path: '/admin/resetCandidateTest',
  //   sidebarName: 'Reset Candidate Test',
  //   roleAction: RoleActions.RESET_CANDIDATE_TEST,
  //   navbarName: 'Admin Panel / Reset Candidate Test',
  //   icon: Brightness7TwoToneIcon,
  //   component: ResetCandidateTest
  // },
  {
    path: '/admin/updateSeniority',
    sidebarName: 'Candidate Seniority Update',
    roleAction: RoleActions.UPDATE_DEVELOPER_SENIORITY,
    navbarName: 'Admin Panel / Candidate Seniority Update',
    icon: Brightness7TwoToneIcon,
    component: UpdateCandidateSeniority
  },
  {
    path: '/admin/mcqStatusUpdate',
    sidebarName: 'Setup MCQ-Role Prefered',
    roleAction: RoleActions.SETUP_MCQ_ROLE_PREFERED,
    navbarName: 'Admin Panel / Setup MCQ-Role Prefered',
    icon: UpdateTwoToneIcon,
    component: MCQStatusUpdate
  },
  {
    path: '/admin/areaRelationship',
    sidebarName: 'Setup Label/Area Relationship',
    roleAction: RoleActions.SETUP_LABEL_AREA_RELATIONSHIP,
    navbarName: 'Admin Panel / Setup Label/Area Relationship',
    icon: LinkIcon,
    component: AreaRelationship
  },
  {
    path: '/admin/mcqSurvey',
    sidebarName: 'MCQ Survey',
    roleAction: RoleActions.MCQ_SURVEY,
    navbarName: 'Admin Panel / MCQ Survey',
    icon: PlaylistAddCheck,
    component: MCQSurvey
  },
  // {
  //   path: '/admin/updateMCQScore',
  //   sidebarName: 'Update MCQ Score',
  //   roleAction: RoleActions.UPDATE_MCQ_SCORE,
  //   navbarName: 'Admin Panel / Update MCQ Challenge Score',
  //   icon: UpdateTwoToneIcon,
  //   component: MCQScoreUpdate
  // },
  {
    path: '/admin/challengeABTestConfig',
    sidebarName: 'Challenge AB Test Configuration',
    roleAction: RoleActions.CHALLENGE_AB_TEST_CONFIGURATION,
    navbarName: 'Admin Panel / Challenge AB Test Configuration',
    icon: TuneIcon,
    component: ChallengeABTestConfig
  },
  // {
  //   path: '/admin/mcqCopyEditor',
  //   sidebarName: 'MCQ Copy Editor',
  //   roleAction: RoleActions.MCQ_COPY_EDITOR,
  //   navbarName: 'Admin Panel / MCQ Copy Editor',
  //   icon: GolfCourseIcon,
  //   component: MCQCopyEditor
  // },
  {
    path: '/admin/impersonateUser',
    sidebarName: 'Login as User',
    roleAction: RoleActions.LOGIN_AS_USER,
    navbarName: 'Admin Panel / Login As User',
    icon: BusinessCenterIcon,
    component: SearchCandidateEmail
  },
  {
    path: '/admin/delete-user-account',
    sidebarName: 'Delete user account',
    roleAction: RoleActions.DELETE_USER,
    navbarName: 'Admin Panel / Delete user account',
    icon: BusinessCenterIcon,
    component: DeleteUserAccount
  },
  {
    path: '/admin/setupStackDemand',
    sidebarName: 'Setup Stack Demand',
    roleAction: RoleActions.SETUP_STACK_DEMAND,
    navbarName: 'Admin Panel / Setup Stack Demand',
    icon: Brightness7TwoToneIcon,
    component: SetupStackDemand
  },
  {
    path: '/admin/interviewEligibility',
    sidebarName: 'Interview Eligibility',
    roleAction: RoleActions.INTERVIEW_ELIGIBILITY,
    navbarName: 'Admin Panel / Interview Eligibility',
    icon: DvrIcon,
    component: InterviewEligibility
  },
  {
    path: '/admin/updateCandidatesStatus',
    sidebarName: 'Update Candidates Status',
    roleAction: RoleActions.UPDATE_CANDIDATES_STATUS,
    navbarName: 'Admin Panel / Update Candidates Status',
    icon: BallotIcon,
    component: UpdateCandidatesStatus
  },
  {
    path: '/admin/live/setup',
    sidebarName: 'Live Code Interview',
    roleAction: RoleActions.LIVE_CODE_INTERVIEW,
    navbarName: 'Admin Panel / Live Code Interview Setup',
    icon: BallotIcon,
    component: LiveCodeAdminContainer
  },
  {
    path: '/admin/setupAvailability',
    sidebarName: 'Interviewer Setup Availability',
    roleAction: RoleActions.SETUP_AVAILABILITY,
    navbarName: 'Admin Panel / Interviewer Setup Availability',
    icon: GolfCourseIcon,
    component: SetupAvailabilityPage
  },
  {
    path: '/admin/account-management',
    sidebarName: 'Admin Account Management',
    roleAction: RoleActions.ACCOUNT_MANAGEMENT,
    navbarName: 'Admin Panel / Admin Account Management',
    icon: BallotIcon,
    component: AdminAccountManagement
  },
  {
    path: '/admin/change-candidate-email',
    sidebarName: 'Change Candidate Email',
    roleAction: RoleActions.CHANGE_CANDIDATE_EMAIL,
    navbarName: 'Admin Panel / Change Candidate Email',
    icon: BallotIcon,
    component: ChangeCandidateEmail
  },
  {
    path: '/admin/booked-event',
    sidebarName: 'List Booked Event',
    roleAction: RoleActions.ADMIN_LIST_BOOKED_EVENT,
    navbarName: 'Admin Panel / List Booked Event',
    icon: BallotIcon,
    component: ScheduledEvent
  },
  {
    path: '/admin/calendar/setup/new',
    sidebarName: 'Admin Calendar Setup',
    roleAction: RoleActions.ADMIN_CALENDAR_SETUP,
    navbarName: 'Admin Panel / Admin Calendar Setup',
    icon: DvrIcon,
    component: CalendarSetup
  },
  {
    path: '/admin/calendar/list',
    sidebarName: 'Admin List Calendars',
    roleAction: RoleActions.ADMIN_LIST_CALENDARS,
    navbarName: 'Admin Panel / Admin List Calendars',
    icon: Brightness7TwoToneIcon,
    component: ListAllCalEvents
  },
  {
    path: '/admin/user-profile-view',
    sidebarName: 'Profile view',
    roleAction: RoleActions.USER_PROFILE_VIEW,
    navbarName: 'Admin Panel / Profile view',
    icon: DvrIcon,
    component: UserProfileViewPage
  },
  {
    path: '/admin/customized-vetting',
    sidebarName: 'Customized Vetting',
    roleAction: RoleActions.VETSMITH,
    navbarName: 'Admin Panel / Customized Vetting',
    icon: DvrIcon,
    component: VetsmithPage
  },
  {
    path: '/admin/create-account-by-resume',
    sidebarName: 'Create account by resume',
    roleAction: RoleActions.USER_PROFILE_VIEW,
    navbarName: 'Admin Panel / Create account by resume',
    icon: DvrIcon,
    component: CreateAccountByResumePage
  },
  {
    path: '/admin/create-accounts',
    sidebarName: 'Create account from csv',
    roleAction: RoleActions.USER_PROFILE_VIEW,
    navbarName: 'Admin Panel / Create account from csv',
    icon: DvrIcon,
    component: CreateAccountsFromCSV
  },
  {
    path: '/admin/s-param-management',
    sidebarName: 'S param management',
    roleAction: RoleActions.USER_PROFILE_VIEW,
    navbarName: 'Admin Panel / S param management',
    icon: DvrIcon,
    component: SParamManagement
  },
  // {
  //   path: '/admin/display-mcq-questions',
  //   sidebarName: 'Display MCQ Questions',
  //   roleAction: RoleActions.USER_PROFILE_VIEW,
  //   navbarName: 'Admin Panel / Display MCQ Questions',
  //   icon: DvrIcon,
  //   component: DisplayMCQQuestionsPage
  // },
  {
    path: '/admin/programming-languages',
    sidebarName: 'Programming languages',
    roleAction: RoleActions.USER_PROFILE_VIEW,
    navbarName: 'Admin Panel / Programming languages',
    icon: DvrIcon,
    component: ProgrammingLanguage
  },
  {
    path: '/admin/interviewer-performance',
    sidebarName: 'Interviewer Performance Reports',
    roleAction: RoleActions.INTERVIEWER_PERFORMANCE_REPORT,
    navbarName: 'Admin Panel / Interviewer Performance Reports',
    icon: DvrIcon,
    component: InterviewerPerformanceReport
  },
  {
    path: '/admin/interviewer-availability',
    sidebarName: 'Interviewer Availability Report',
    roleAction: RoleActions.INTERVIEWER_AVAILABILITY_REPORT,
    navbarName: 'Admin Panel / Interviewer Availability Report',
    icon: Brightness7TwoToneIcon,
    component: InterviewerAvailabilityReport
  },
  {
    path: '/admin/user-profile-view',
    sidebarName: 'Profile view',
    roleAction: RoleActions.USER_PROFILE_VIEW,
    navbarName: 'Admin Panel / Profile view',
    icon: DvrIcon,
    component: UserProfileViewPage
  },
  {
    path: '/admin/setup-skill-relationship',
    sidebarName: 'Setup skill relationship',
    roleAction: RoleActions.SETUP_SKILL_RELATIONSHIP,
    navbarName: 'Admin Panel / Setup skill relationship',
    icon: DvrIcon,
    component: SetupSkillRelationship
  },
  {
    path: '/admin/setup-skill-mcq-relationship',
    sidebarName: 'Setup skill MCQ relationship',
    roleAction: RoleActions.SETUP_SKILL_MCQ_RELATIONSHIP,
    navbarName: 'Admin Panel / Setup skill MCQ relationship',
    icon: DvrIcon,
    component: SetupSkillRelationship
  },
  {
    path: '/admin/setup-demand-stack-skill',
    sidebarName: 'Setup demand stack skill',
    roleAction: RoleActions.SETUP_DEMAND_STACK_SKILL,
    navbarName: 'Admin Panel / Setup demand stack skill',
    icon: Brightness7TwoToneIcon,
    component: SetupStackDemandSkill
  },
  {
    path: '/admin/job_search',
    sidebarName: 'Job Search',
    roleAction: RoleActions.JOB_SEARCH,
    navbarName: 'Admin Panel / Job Search',
    icon: DvrIcon,
    component: JobSearch
  },
  {
    path: '/admin/change-password',
    sidebarName: 'Change password',
    navbarName: 'Admin Panel / Change password',
    icon: BallotIcon,
    component: AdminChangePassword
  },
  {
    path: '/admin/referral-statistics',
    sidebarName: 'Referral Statistics',
    navbarName: 'Admin Panel / Referral Statistics',
    icon: BallotIcon,
    component: AdminReferralStatistics
  },
  {
    path: '/admin/create-test-account',
    sidebarName: 'Create Test Account',
    navbarName: 'Admin Panel / Create Test Account',
    icon: DvrIcon,
    component: CreateTestAccount
  },
  {
    path: '/admin/cms',
    sidebarName: 'Content Management System (CMS)',
    navbarName: 'Admin Panel / CMS',
    icon: DvrIcon,
    component: ContentManagementSystem
  },
  // {
  //   path: '/admin/recommendation',
  //   sidebarName: 'Setup MCQ Recommendation',
  //   navbarName: 'Admin Panel / Setup MCQ Recommendation',
  //   icon: DvrIcon,
  //   component: SetupRecommendationMCQ
  // },
  {
    path: '/admin/search-acc-video',
    sidebarName: 'Search ACC Videos',
    navbarName: 'Admin Panel / Search ACC Videos',
    icon: BallotIcon,
    component: FetchACCVideo
  },
  {
    path: '/admin/cloud-certification-verification',
    sidebarName: 'Cloud Certification Verification',
    navbarName: 'Admin Panel / Cloud Certification Verification',
    icon: DvrIcon,
    component: CloudCertificationVerification
  },
  {
    path: '/admin/cloud-certificates',
    sidebarName: 'Cloud Certificates',
    navbarName: 'Admin Panel / Cloud Certificates',
    icon: BallotIcon,
    component: CloudCertificates
  },
  {
    path: '/admin/fast-track-developers',
    sidebarName: 'Fast Track Developers',
    navbarName: 'Admin Panel / Fast Track Developers',
    icon: BallotIcon,
    component: FastTrackDevelopers
  },
  {
    path: '/admin/login',
    sidebarName: 'Logout',
    icon: ExitToAppIcon,
    navLinkProps: {
      onClick: e => {
        localStorage.clear();
        window.location.reload();
      }
    }
  }
  /* End legacy_components */
]

export default AdminPanelRoutes
